import { APIResponse } from '@teko/api';
import type {
  GetCOVOrderDetailParams,
  GetCOVOrdersParams,
  GetOrderParams,
  GetPromotionsParams,
  IMarketplaceOrderDetail,
  IOrderService,
  OrderService,
  VatInfo,
  VatOrderInfo,
  GetUserTerminalParams,
  UserTerminal,
  GetTerminalParams,
  Terminal,
  ITerminal,
  IGetTerminalsParams,
  ITerminalsByLocation,
  ICancelOrderBody,
  GetTerminalsByAddress,
} from '@teko/order';
import {
  GetOrdersV2Params,
  GetOrdersV2Response,
  IOrderDetailV2,
  IGetReturnReasonsResponse,
} from '@teko/order/v2';

class OrderServiceWrapper implements IOrderService {
  private baseUrl: string;
  private servicePromise: Promise<OrderService> | undefined;
  private language: string | undefined;

  constructor(baseUrl: string, language?: string) {
    this.baseUrl = baseUrl;
    this.language = language;
  }

  private async initializeService(): Promise<OrderService> {
    if (!this.servicePromise) {
      this.servicePromise = import('@teko/order').then(
        (mod) => new mod.OrderService(this.baseUrl, this.language),
      );
    }
    return this.servicePromise;
  }

  async getOrder(params: GetOrderParams): Promise<IMarketplaceOrderDetail> {
    const service = await this.initializeService();
    return service.getOrder(params);
  }

  async getOrders(params: GetCOVOrdersParams) {
    const service = await this.initializeService();
    return service.getOrders(params);
  }

  async getOrderDetail(params: GetCOVOrderDetailParams) {
    const service = await this.initializeService();
    return service.getOrderDetail(params);
  }

  async importOrder(orderId: string) {
    const service = await this.initializeService();
    return service.importOrder(orderId);
  }

  async updateVatInfo(orderId: string, vatInfo: VatInfo) {
    const service = await this.initializeService();
    return service.updateVatInfo(orderId, vatInfo);
  }

  async updateOrderVatInfo(orderId: string, vatInfo: VatOrderInfo) {
    const service = await this.initializeService();
    return service.updateOrderVatInfo(orderId, vatInfo);
  }

  async getPromotions(params: GetPromotionsParams) {
    const service = await this.initializeService();
    return service.getPromotions(params);
  }

  async getOrderDetailV2(
    orderId: string,
    platformId: number,
  ): Promise<IOrderDetailV2> {
    const service = await this.initializeService();
    return service.getOrderDetailV2(orderId, platformId);
  }

  async getOrdersV2(params: GetOrdersV2Params): Promise<GetOrdersV2Response> {
    const service = await this.initializeService();
    return service.getOrdersV2(params);
  }

  async getUserTerminal(params: GetUserTerminalParams): Promise<UserTerminal> {
    const service = await this.initializeService();
    return service.getUserTerminal(params);
  }

  async getTerminal(params: GetTerminalParams): Promise<APIResponse<Terminal>> {
    const service = await this.initializeService();
    return service.getTerminal(params);
  }

  async getPriorityTerminal(
    params: GetTerminalParams,
  ): Promise<APIResponse<Terminal>> {
    const service = await this.initializeService();
    return service.getPriorityTerminal(params);
  }

  async getListOfTerminals(
    params: IGetTerminalsParams,
  ): Promise<APIResponse<{ terminals: ITerminal[] }>> {
    const service = await this.initializeService();
    return service.getListOfTerminals(params);
  }

  async getListOfTerminalsByLocation(
    params: GetTerminalParams,
  ): Promise<APIResponse<ITerminalsByLocation>> {
    const service = await this.initializeService();
    return service.getListOfTerminalsByLocation(params);
  }

  async getTerminalsByAddress(
    params: GetTerminalsByAddress,
  ): Promise<APIResponse<ITerminalsByLocation>> {
    const service = await this.initializeService();
    return service.getTerminalsByAddress(params);
  }

  async getReturnReasonsList(
    platformId: number,
  ): Promise<IGetReturnReasonsResponse> {
    const service = await this.initializeService();
    return service.getReturnReasonsList(platformId);
  }

  async requestCancelOrder(body: ICancelOrderBody): Promise<void> {
    const service = await this.initializeService();
    return service.requestCancelOrder(body);
  }
}

export default OrderServiceWrapper;
